import React, { useMemo } from 'react';
import { faRefresh, faUndo } from '@fortawesome/free-solid-svg-icons';
import ActionToolbar from '../common/ActionToolbar';
import { UI_COLORS } from '../../utils/colors';
import { useTranslation } from 'react-i18next';
import { todayIST } from '../../utils/dates';

const VehicleTrackingFilters = ({
  searchTerm,
  onSearchChange,
  fromDate,
  onFromDateChange,
  toDate,
  onToDateChange,
  status,
  onStatusChange,
  onRefresh,
  onReset,
  refreshLoading = false,
}) => {
  const { t } = useTranslation();
  const actions = useMemo(() => [
    {
      key: 'reset',
      label: t('common.actions.reset'),
      icon: faUndo,
      onClick: onReset,
      variant: 'ghost',
      className: 'text-gray-500 hover:text-gray-700'
    },
    {
      key: 'refresh',
      label: t('common.buttons.refresh'),
      icon: faRefresh,
      onClick: onRefresh,
      disabled: refreshLoading,
      variant: 'ghost',
      spinner: refreshLoading,
    }
  ], [onRefresh, onReset, refreshLoading, t]);

  const statusOptions = useMemo(() => [
    { value: '', label: t('common.filters.allStatuses') },
    { value: 'pending', label: t('common.statuses.pending') },
    { value: 'supervisor_assigned', label: t('common.statuses.supervisorAssigned') },
    { value: 'loading', label: t('common.statuses.loading') },
    { value: 'loaded', label: t('common.statuses.loaded') },
    { value: 'in_transit', label: t('common.statuses.inTransit') },
    { value: 'delivered', label: t('common.statuses.delivered') },
  ], [t]);

  const filters = useMemo(() => [
    {
      key: 'status',
      type: 'select',
      label: t('common.filters.status'),
      value: status,
      options: statusOptions,
      onChange: onStatusChange,
    },
    // Custom date filters as React elements since ActionToolbar 
    // descriptor doesn't explicitly handle 'date' type in its switch but accepts elements.
    (
      <div key="from-date" className="flex items-center gap-2">
        <label className="text-sm font-medium" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('common.filters.from')}</label>
        <input
          type="date"
          value={fromDate}
          max={todayIST()}
          onChange={(e) => onFromDateChange(e.target.value)}
          className="px-3 py-2 rounded-xl border-2 outline-none focus:ring-2 transition-all"
          style={{
            backgroundColor: UI_COLORS.FORMS.INPUT.background,
            borderColor: UI_COLORS.FORMS.INPUT.border,
            color: UI_COLORS.FORMS.INPUT.text,
            fontSize: '14px'
          }}
        />
      </div>
    ),
    (
      <div key="to-date" className="flex items-center gap-2">
        <label className="text-sm font-medium" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('common.filters.to')}</label>
        <input
          type="date"
          value={toDate}
          max={todayIST()}
          onChange={(e) => onToDateChange(e.target.value)}
          className="px-3 py-2 rounded-xl border-2 outline-none focus:ring-2 transition-all"
          style={{
            backgroundColor: UI_COLORS.FORMS.INPUT.background,
            borderColor: UI_COLORS.FORMS.INPUT.border,
            color: UI_COLORS.FORMS.INPUT.text,
            fontSize: '14px'
          }}
        />
      </div>
    )
  ], [status, fromDate, toDate, onStatusChange, onFromDateChange, onToDateChange, statusOptions, t]);

  return (
    <ActionToolbar
      searchValue={searchTerm}
      onSearchChange={onSearchChange}
      searchPlaceholder={t('vehicleTracking.filters.searchPlaceholder')}
      filters={filters}
      actions={actions}
    />
  );
};

export default VehicleTrackingFilters;
